import { colorize, detectFileType, type ColorName } from './colorScheme'

// Main syntax highlighter function
export function highlightSyntax(content: string, filename?: string): string {
  if (!filename) {
    // Check for ANSI color codes if no filename
    return processAnsiCodes(content)
  }

  const fileType = detectFileType(filename)

  switch (fileType) {
    case 'json':
      return highlightJSON(content)
    case 'markdown':
      return highlightMarkdown(content)
    case 'javascript':
    case 'typescript':
      return highlightJavaScript(content)
    case 'shell':
      return highlightShell(content)
    case 'log':
      return highlightLog(content)
    default:
      return processAnsiCodes(content)
  }
}

// JSON syntax highlighting
function highlightJSON(content: string): string {
  try {
    // Validate JSON first
    JSON.parse(content)

    // Highlight different JSON elements
    let highlighted = content

    // String values (including keys)
    highlighted = highlighted.replace(
      /"([^"\\]*(\\.[^"\\]*)*)"/g,
      (match) => {
        // Check if it's a key (followed by :) or value
        return colorize(match, 'string')
      }
    )

    // Numbers
    highlighted = highlighted.replace(
      /\b(-?\d+\.?\d*([eE][+-]?\d+)?)\b/g,
      (match) => colorize(match, 'number')
    )

    // Booleans
    highlighted = highlighted.replace(
      /\b(true|false)\b/g,
      (match) => colorize(match, 'boolean')
    )

    // null
    highlighted = highlighted.replace(
      /\bnull\b/g,
      (match) => colorize(match, 'keyword')
    )

    // Punctuation
    highlighted = highlighted.replace(
      /([{}[\],:])/g,
      (match) => colorize(match, 'punctuation')
    )

    return highlighted
  } catch {
    // Invalid JSON, return as-is
    return content
  }
}

// Markdown syntax highlighting
function highlightMarkdown(content: string): string {
  let highlighted = content

  // Headers
  highlighted = highlighted.replace(
    /^(#{1,6})\s+(.+)$/gm,
    (_, hashes, text) =>
      colorize(hashes, 'keyword') + ' ' + colorize(text, 'brightCyan')
  )

  // Bold
  highlighted = highlighted.replace(
    /\*\*(.+?)\*\*/g,
    (_, text) => colorize('**', 'operator') + colorize(text, 'brightWhite') + colorize('**', 'operator')
  )

  // Italic
  highlighted = highlighted.replace(
    /\*(.+?)\*/g,
    (_, text) => colorize('*', 'operator') + colorize(text, 'white') + colorize('*', 'operator')
  )

  // Inline code
  highlighted = highlighted.replace(
    /`([^`]+)`/g,
    (_, code) => colorize('`', 'punctuation') + colorize(code, 'green') + colorize('`', 'punctuation')
  )

  // Links
  highlighted = highlighted.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, text, url) =>
      colorize('[', 'punctuation') +
      colorize(text, 'brightBlue') +
      colorize('](', 'punctuation') +
      colorize(url, 'cyan') +
      colorize(')', 'punctuation')
  )

  // Lists
  highlighted = highlighted.replace(
    /^([-*+]|\d+\.)\s+/gm,
    (match) => colorize(match, 'keyword')
  )

  // Code blocks
  highlighted = highlighted.replace(
    /```(\w*)\n([\s\S]*?)```/g,
    (_, lang, code) =>
      colorize('```' + lang, 'comment') +
      '\n' +
      colorize(code, 'green') +
      colorize('```', 'comment')
  )

  return highlighted
}

// JavaScript/TypeScript syntax highlighting (basic)
function highlightJavaScript(content: string): string {
  let highlighted = content

  // Comments
  highlighted = highlighted.replace(
    /(\/\/.*$|\/\*[\s\S]*?\*\/)/gm,
    (match) => colorize(match, 'comment')
  )

  // Strings
  highlighted = highlighted.replace(
    /(['"`])(?:(?=(\\?))\2.)*?\1/g,
    (match) => colorize(match, 'string')
  )

  // Keywords
  const keywords =
    'const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|import|export|from|default|class|extends|async|await|try|catch|finally|throw|new|this|super|static|public|private|protected|interface|type|enum'

  highlighted = highlighted.replace(
    new RegExp(`\\b(${keywords})\\b`, 'g'),
    (match) => colorize(match, 'keyword')
  )

  // Numbers
  highlighted = highlighted.replace(
    /\b(\d+\.?\d*)\b/g,
    (match) => colorize(match, 'number')
  )

  // Booleans
  highlighted = highlighted.replace(
    /\b(true|false|null|undefined)\b/g,
    (match) => colorize(match, 'boolean')
  )

  // Functions (basic detection)
  highlighted = highlighted.replace(
    /\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g,
    (_match, name) => colorize(name, 'function') + colorize('(', 'punctuation')
  )

  return highlighted
}

// Shell script highlighting
function highlightShell(content: string): string {
  let highlighted = content

  // Comments
  highlighted = highlighted.replace(
    /#.*$/gm,
    (match) => colorize(match, 'comment')
  )

  // Strings
  highlighted = highlighted.replace(
    /(['"])(?:(?=(\\?))\2.)*?\1/g,
    (match) => colorize(match, 'string')
  )

  // Commands (common shell commands)
  const commands =
    'echo|cd|ls|pwd|cat|grep|sed|awk|find|mkdir|rm|cp|mv|chmod|chown|export|source|alias'

  highlighted = highlighted.replace(
    new RegExp(`\\b(${commands})\\b`, 'g'),
    (match) => colorize(match, 'keyword')
  )

  // Variables
  highlighted = highlighted.replace(
    /\$\{?[a-zA-Z_][a-zA-Z0-9_]*\}?/g,
    (match) => colorize(match, 'variable')
  )

  return highlighted
}

// Log file highlighting
function highlightLog(content: string): string {
  let highlighted = content

  // Error patterns
  highlighted = highlighted.replace(
    /\b(ERROR|FATAL|FAIL|Failed|Exception|Error).*$/gim,
    (match) => colorize(match, 'red')
  )

  // Warning patterns
  highlighted = highlighted.replace(
    /\b(WARN|WARNING|Deprecated).*$/gim,
    (match) => colorize(match, 'yellow')
  )

  // Success patterns
  highlighted = highlighted.replace(
    /\b(SUCCESS|PASS|OK|Passed|Success).*$/gim,
    (match) => colorize(match, 'green')
  )

  // Info patterns
  highlighted = highlighted.replace(
    /\b(INFO|DEBUG|TRACE).*$/gim,
    (match) => colorize(match, 'cyan')
  )

  // Timestamps
  highlighted = highlighted.replace(
    /\b\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/g,
    (match) => colorize(match, 'brightBlack')
  )

  return highlighted
}

// Process ANSI color codes (for echo command support)
function processAnsiCodes(content: string): string {
  // Simple ANSI color code support
  const ansiColorMap: Record<string, ColorName> = {
    '30': 'black',
    '31': 'red',
    '32': 'green',
    '33': 'yellow',
    '34': 'blue',
    '35': 'magenta',
    '36': 'cyan',
    '37': 'white',
    '90': 'brightBlack',
    '91': 'brightRed',
    '92': 'brightGreen',
    '93': 'brightYellow',
    '94': 'brightBlue',
    '95': 'brightMagenta',
    '96': 'brightCyan',
    '97': 'brightWhite',
  }

  let result = content

  // Replace ANSI codes with HTML spans
  result = result.replace(
    /\x1b\[(\d+)m([^\x1b]*)/g,
    (_, code, text) => {
      const color = ansiColorMap[code]
      return color ? colorize(text, color) : text
    }
  )

  // Remove reset codes
  result = result.replace(/\x1b\[0m/g, '')

  return result
}

// Apply syntax highlighting safely (for use in React components)
export function applySyntaxHighlighting(
  content: string,
  filename?: string
): { __html: string } {
  const highlighted = highlightSyntax(content, filename)
  return { __html: highlighted }
}
